import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BankAccountsApi, NotificationManager } from '@atlas/core';
import { Button, DefaultPopup, HeaderTitle, Label, Loader, Radio } from '@atlas/ui';

import { ACCOUNT_NUMBERS_POPUP_TYPES } from 'common/constants';
import PaymentMethodItem from 'src/components/ClientProfile/PaymentTab/PaymentMethodItem';

export const AccountNumbersPopup = ({
  data: {
    amount,
    onDepositStatusError,
    onWithdrawalStatusError,
    paymentAction,
    paymentMethod,
    paymentSubmit,
    pmId,
    type,
  },
  id,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeAccountIndex, setActiveAccountIndex] = useState(0);

  useEffect(() => {
    BankAccountsApi.getBankAccounts()
      .then(data => {
        setAccountNumbers(data || []);
        const defaultIndex = data?.findIndex(a => a.useByDefault);

        if (defaultIndex !== -1) {
          setActiveAccountIndex(defaultIndex);
        } else if (data?.length) {
          setActiveAccountIndex(0);
        }
      })
      .catch(() => {
        setAccountNumbers([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const errorAction = useCallback(
    error => {
      type === ACCOUNT_NUMBERS_POPUP_TYPES.DEPOSIT
        ? onDepositStatusError(error)
        : onWithdrawalStatusError(error);
    },
    [onDepositStatusError, onWithdrawalStatusError, type],
  );

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    const accountNumbersData = accountNumbers[activeAccountIndex];
    if (!accountNumbersData) return;

    const bankAccountInfo = {};
    if (accountNumbersData.bankCode) bankAccountInfo.bankCode = accountNumbersData.bankCode;
    if (accountNumbersData.branchCode) bankAccountInfo.branchCode = accountNumbersData.branchCode;
    if (accountNumbersData.number) bankAccountInfo.bankAccountNumber = accountNumbersData.number;
    if (accountNumbersData.accountType)
      bankAccountInfo.bankAccountType = accountNumbersData.accountType;

    setIsSubmitting(true);

    handleClose();
    try {
      const res = await paymentAction({
        amount,
        bankAccountInfo,
        pmId,
      });

      paymentSubmit(res);
    } catch (error) {
      errorAction(error);
    }
  }, [
    isSubmitting,
    accountNumbers,
    handleClose,
    activeAccountIndex,
    paymentAction,
    amount,
    pmId,
    paymentSubmit,
    errorAction,
  ]);

  const isPossibleSelect = accountNumbers.length > 1;

  const contentCls = cx('withdrawal-pix__content', 'withdrawal-pix__content-accounts', {
    'withdrawal-pix__content-accounts-one': !isPossibleSelect,
  });

  if (isLoading) {
    return (
      <DefaultPopup
        className="withdrawal-pix"
        headerTitle={type}
        headerTitlePosition="left"
        onClose={handleClose}
      >
        <Loader />
      </DefaultPopup>
    );
  }

  return (
    <DefaultPopup
      className="withdrawal-pix"
      headerTitle={type}
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big withdrawal-pix__title">
        <PaymentMethodItem amount={amount} paymentMethod={paymentMethod} showAmount />
      </HeaderTitle>

      <div className={contentCls}>
        {isPossibleSelect && (
          <Label
            className="popup__title withdrawal-pix__description-title"
            message="withdrawal_kyc_select_account_number"
          >
            {':'}
          </Label>
        )}
        <div>
          {accountNumbers.map(({ accountType, bankName, branchCode, number }, index) => (
            <button
              className="withdrawal-pix__description"
              key={number || `account-${index}`}
              onClick={() => setActiveAccountIndex(index)}
              type="button"
            >
              <div className="withdrawal-pix__radio">
                {isPossibleSelect && (
                  <Radio
                    active={index === activeAccountIndex}
                    className="dropdown-list-item"
                    id={`account-${index}`}
                    text=""
                  />
                )}
                <Label
                  className="withdrawal-pix__description-label"
                  message="account_numbers_account_number"
                >
                  {` ${index + 1}`}
                </Label>
              </div>
              <div className="withdrawal-pix__description-account">
                {!!bankName && (
                  <Label className="sub-title-text" message="bank_name">
                    {`: ${bankName}`}
                  </Label>
                )}
                {!!branchCode && (
                  <Label className="sub-title-text" message="branch_code">
                    {`: ${branchCode}`}
                  </Label>
                )}
                {!!number && (
                  <Label className="sub-title-text" message="account_number">
                    {`: ${number}`}
                  </Label>
                )}
                {!!accountType && (
                  <Label className="sub-title-text" message="account_type">
                    {`: ${accountType}`}
                  </Label>
                )}
              </div>
            </button>
          ))}
        </div>

        <Button
          className="withdrawal-pix__button"
          disabled={!accountNumbers.length}
          label={type}
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

AccountNumbersPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.string,
    onDepositStatusError: PropTypes.func,
    onWithdrawalStatusError: PropTypes.func,
    paymentAction: PropTypes.func,
    paymentMethod: PropTypes.object,
    paymentSubmit: PropTypes.func,
    pmId: PropTypes.string,
    type: PropTypes.oneOf([
      ACCOUNT_NUMBERS_POPUP_TYPES.DEPOSIT,
      ACCOUNT_NUMBERS_POPUP_TYPES.WITHDRAWAL,
    ]),
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
